import Tabs from "@mui/material/Tabs";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import BreadCrumbs from "Utils/Common/Breadcrumb";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import {
  type PermissionObject,
} from "Shared/Common/Permissions/PermissionComponent";
import { sortById, type SortingTypeI } from "Utils/consts";
import IdLisRunService, {
  type ApplyIdLisRunAccessionsRequest,
  type IdLisRunBulkAccessionUpdateResponse,
  buildIdLisRunRunsLookupPayload,
  buildIdLisRunSearchPayload,
  type CreateIdLisRunRequest,
  type IdLisRunListResponse,
  type IdLisRunPagedResponse,
  type IdLisRunSearchFilters,
  type IdLisRunTemplatesResponse,
  type RequestResponse,
} from "Services/InfectiousDisease/IdLisRunService";
import {
  DUPLICATE_ACCESSION_ERROR_TEXT,
  ID_LIS_MODULE_NAME,
  RUNS_FILTER_LOOKUP_QUERY_KEY,
  RUN_FILTER_FIELDS,
  RUN_FILTER_LABELS,
  RUN_TAB_CONFIG,
  TabSelected,
  VIEW_CREATE_RUN_PERMISSION_PAGE,
  a11yProps,
  buildDuplicateAccessionErrors,
  getUniqueOptions,
  initialSearch,
  mapApiRunToGridRow,
  normalizePermissionValue,
  type RunGridRow,
} from "./viewCreateRunHelpers";
import CreateRunPanel from "./components/CreateRunPanel";
import RunDetailPanel from "./components/RunDetailPanel";
import { printRunPlate } from "./printRunPlate";
import CompleteRunsTab from "./tabs/CompleteRunsTab";
import DeletedRunsTab from "./tabs/DeletedRunsTab";
import ViewRunsTab from "./tabs/ViewRunsTab";

const IDViewCreateRun: React.FC = () => {
  const { t } = useLang();

  const {
    data: runTemplatesBody,
    isLoading: isRunTemplatesLoading,
    isError: isRunTemplatesError,
    error: runTemplatesError,
  } = useQuery(
    "idlis-run-templates",
    async (): Promise<IdLisRunTemplatesResponse | undefined> => {
      const res = await IdLisRunService.GetRunTemplates();
      return res?.data;
    },
    {
      refetchOnWindowFocus: false,
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load run templates";
        toast.error(t(message));
      },
    }
  );

  const {
    data: runsFilterLookupRows,
    isError: isRunsLookupError,
    error: runsLookupError,
  } = useQuery(
    RUNS_FILTER_LOOKUP_QUERY_KEY,
    async (): Promise<IdLisRunListResponse[]> => {
      const res = await IdLisRunService.GetRuns(buildIdLisRunRunsLookupPayload());
      const body = res?.data as IdLisRunPagedResponse | undefined;
      const statusCode = Number(body?.statusCode);

      if (statusCode >= 400) {
        throw new Error(
          (body as { message?: string } | undefined)?.message ||
          "Unable to load runs"
        );
      }

      return body?.data ?? [];
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
      onError: (error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load runs";
        toast.error(t(message));
      },
    }
  );

  const templateTypeOptions =
    runTemplatesBody?.PlateSetup?.map((opt) => ({
      value: String(opt.value),
      label: String(opt.label ?? ""),
    })) ?? [];

  const permissions = useSelector(
    (state: any) =>
      state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.permissions ||
      []
  ) as PermissionObject[];

  const hasViewCreateRunPermission = useCallback(
    (permissionIdentifier: string) =>
      permissions.some(
        (permission) =>
          normalizePermissionValue(permission.subject) ===
            normalizePermissionValue(VIEW_CREATE_RUN_PERMISSION_PAGE) &&
          normalizePermissionValue(permission.moduleName) ===
            normalizePermissionValue(ID_LIS_MODULE_NAME) &&
          normalizePermissionValue(permission.action) ===
            normalizePermissionValue(permissionIdentifier)
      ),
    [permissions]
  );

  const availableTabs = useMemo(
    () =>
      RUN_TAB_CONFIG.filter((tab) =>
        // TODO: Will uncomment it once confirm from PO
        // hasViewCreateRunPermission(tab.permissionIdentifier)
    tab
      ),
    [hasViewCreateRunPermission]
  );

  const defaultTabValue = availableTabs[0]?.value ?? 0;
  // ── Tabs (MUI, same pattern as ManageSalesRep) ────────────────────────────
  const [tabIndex, setTabIndex] = useState(0);
  useEffect(() => {
    if (!availableTabs.length) return;
    if (!availableTabs.some((tab) => tab.value === tabIndex)) {
      setTabIndex(defaultTabValue);
    }
  }, [availableTabs, defaultTabValue, tabIndex]);
  const tabIndexRef = useRef(tabIndex);
  useEffect(() => {
    tabIndexRef.current = tabIndex;
  }, [tabIndex]);

  // ── Add New Plate panel visibility ──────────────────────────────────────────
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [deleteConfirmRun, setDeleteConfirmRun] = useState<RunGridRow | null>(
    null
  );

  // ── Add New Plate form state ─────────────────────────────────────────────────
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplateType, setNewTemplateType] = useState<{
    value: string;
    label: string;
  } | null>(null);

  // ── Sorting ─────────────────────────────────────────────────────────────────
  const [sort, setSort] = useState<SortingTypeI>(sortById);

  // ── Search ──────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState<IdLisRunSearchFilters>({ ...initialSearch });
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const searchRef = useRef(search);
  useEffect(() => {
    searchRef.current = search;
  }, [search]);

  useEffect(() => {
    const tags = RUN_FILTER_FIELDS.filter((field) =>
      String(search[field] ?? "").trim()
    ).map((field) => t(RUN_FILTER_LABELS[field]));
    setSearchedTags(tags);
  }, [search, t]);

  // ── Paged run list (server; same payload pattern as PlateSetup search) ─────
  const [runsPageData, setRunsPageData] = useState<IdLisRunListResponse[]>([]);
  const [isRunsLoading, setIsRunsLoading] = useState(false);
  const [runsListError, setRunsListError] = useState<string | null>(null);

  // ── Expanded row ────────────────────────────────────────────────────────────
  const [expandedId, setExpandedId] = useState<string | null>(null);

  /** Editable accession text per run (shown in inputs + grouped column headers). */
  const [runAccessionDrafts, setRunAccessionDrafts] = useState<
    Record<string, string[]>
  >({});
  const [runAccessionErrors, setRunAccessionErrors] = useState<
    Record<string, Record<number, string>>
  >({});

  // ── Pagination ───────────────────────────────────────────────────────────────
  const {
    curPage,
    showPage,
    nextPage,
    prevPage,
    pageSize,
    totalPages,
    pageNumbers,
    setPageSize,
    setCurPage,
    total,
    setTotal,
  } = usePagination();

  const loadData = useCallback(
    async (
      page: number,
      size: number,
      sortState: SortingTypeI,
      query: IdLisRunSearchFilters,
      tabOverride?: number
    ) => {
      const tab = tabOverride ?? tabIndexRef.current;
      setIsRunsLoading(true);
      setRunsListError(null);
      try {
        const payload = buildIdLisRunSearchPayload({
          pageNumber: page,
          pageSize: size,
          sort: sortState,
          filters: query,
          tabIndex: tab,
        });
        const res =
          tab === 2
            ? await IdLisRunService.GetDeletedRuns(payload)
            : await IdLisRunService.GetRuns(payload);
        const body = res?.data as IdLisRunPagedResponse | undefined;
        const statusCode = Number(body?.statusCode);

        if (statusCode >= 400) {
          throw new Error(
            (body as { message?: string } | undefined)?.message ||
            "Unable to load runs"
          );
        }

        setRunsPageData(body?.data ?? []);
        setTotal(body?.total ?? 0);
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load runs";
        setRunsListError(message);
        toast.error(t(message));
        setRunsPageData([]);
        setTotal(0);
      } finally {
        setIsRunsLoading(false);
      }
    },
    [setTotal, t]
  );

  useEffect(() => {
    loadData(curPage, pageSize, sort, searchRef.current);
  }, []);

  const isFirstPageEffect = useRef(true);
  useEffect(() => {
    if (isFirstPageEffect.current) {
      isFirstPageEffect.current = false;
      return;
    }
    loadData(curPage, pageSize, sort, searchRef.current);
  }, [curPage, pageSize]);

  const runRows = useMemo(
    () => runsPageData.map(mapApiRunToGridRow),
    [runsPageData]
  );

  const lookupGridRows = useMemo(
    () => (runsFilterLookupRows ?? []).map(mapApiRunToGridRow),
    [runsFilterLookupRows]
  );

  const runTypeOptions = getUniqueOptions(
    lookupGridRows.map((row) => row.runType)
  );
  const runTemplateOptions = getUniqueOptions(
    lookupGridRows.map((row) => row.runTemplate)
  );

  useEffect(() => {
    if (!expandedId) return;
    const row = runRows.find((r) => r.id === expandedId);
    if (!row) return;
    setRunAccessionDrafts((prev) => ({
      ...prev,
      [expandedId]: [...row.accessionSlots],
    }));
    setRunAccessionErrors((prev) => ({
      ...prev,
      [expandedId]: {},
    }));
  }, [expandedId, runRows]);

  const resetCreateRunForm = () => {
    setNewTemplateName("");
    setNewTemplateType(null);
  };

  const createRunMutation = useMutation(
    (payload: CreateIdLisRunRequest) => IdLisRunService.CreateRun(payload),
    {
      onSuccess: (response) => {
        const body = response?.data;
        const statusCode = Number(body?.statusCode);

        if (!statusCode || statusCode < 200 || statusCode >= 300) {
          toast.error(t(body?.message || "Unable to create run"));
          return;
        }

        const cleared = { ...initialSearch };
        loadData(1, pageSize, { ...sortById }, cleared, 0);
        setSort({ ...sortById });
        setSearch(cleared);
        searchRef.current = cleared;

        toast.success(t(body?.message || "Run created successfully."));
        setShowAddPanel(false);
        resetCreateRunForm();
        setTabIndex(0);
        setCurPage(1);
      },
      onError: (error: any) => {
        toast.error(
          t(
            error?.response?.data?.message ||
            error?.message ||
            "Unable to create run"
          )
        );
      },
    }
  );

  const applyAccessionsMutation = useMutation(
    (payload: ApplyIdLisRunAccessionsRequest) =>
      IdLisRunService.ApplyAccessions(payload)
  );

  const completeRunMutation = useMutation((idLISRunId: number) =>
    IdLisRunService.CompleteRun({ idLISRunId })
  );

  const softDeleteRunMutation = useMutation((idLISRunId: number) =>
    IdLisRunService.SoftDeleteRun({ idLISRunId })
  );

  const handleApplyAccessions = async (row: RunGridRow) => {
    const accessionSlots = runAccessionDrafts[row.id] ?? row.accessionSlots;
    const duplicateFieldErrors = buildDuplicateAccessionErrors(
      accessionSlots,
      DUPLICATE_ACCESSION_ERROR_TEXT
    );

    if (Object.keys(duplicateFieldErrors).length > 0) {
      setRunAccessionErrors((prev) => ({
        ...prev,
        [row.id]: duplicateFieldErrors,
      }));
      toast.error(
        t("Accession already exists. Please enter unique accession numbers.")
      );
      return;
    }

    const accessions = accessionSlots
      .map((value, index) => ({
        accessionSlotIndex: index + 1,
        accessionNumber: value.trim(),
      }))
      .filter((item) => Boolean(item.accessionNumber));

    if (accessions.length === 0) {
      toast.error(t("Please enter at least one accession number."));
      return;
    }

    try {
      const res = await applyAccessionsMutation.mutateAsync({
        idLISRunId: row.idLISRunId,
        accessions,
      });

      const body =
        res?.data as
        | RequestResponse<IdLisRunBulkAccessionUpdateResponse>
        | undefined;
      const statusCode = Number(body?.statusCode);
      const itemResults = body?.data?.items ?? [];

      const invalidFieldErrors: Record<number, string> = {};
      itemResults.forEach((item) => {
        if (!item.isValid) {
          invalidFieldErrors[item.accessionSlotIndex - 1] =
            item.message || "Invalid accession number.";
        }
      });

      setRunAccessionErrors((prev) => ({
        ...prev,
        [row.id]: invalidFieldErrors,
      }));

      if (!statusCode || statusCode < 200 || statusCode >= 300) {
        toast.error(t(body?.message || "Some accession numbers are invalid."));
        return;
      }

      toast.success(t(body?.message || "Accessions applied successfully."));
      await loadData(curPage, pageSize, sort, searchRef.current);
    } catch (error: any) {
      toast.error(
        t(
          error?.response?.data?.message ||
          error?.message ||
          "Unable to apply accessions"
        )
      );
    }
  };

  const handleCompleteRun = async (row: RunGridRow) => {
    try {
      const res = await completeRunMutation.mutateAsync(row.idLISRunId);
      const body = res?.data as RequestResponse<boolean> | undefined;
      const statusCode = Number(body?.statusCode);

      if (!statusCode || statusCode < 200 || statusCode >= 300) {
        toast.error(t(body?.message || "Unable to complete run"));
        return;
      }

      toast.success(t(body?.message || "Run completed successfully."));
      setExpandedId(null);
      await loadData(curPage, pageSize, sort, searchRef.current);
    } catch (error: any) {
      toast.error(
        t(
          error?.response?.data?.message ||
          error?.message ||
          "Unable to complete run"
        )
      );
    }
  };

  const handleDeleteRun = (row: RunGridRow) => {
    setDeleteConfirmRun(row);
  };

  const handleDeleteConfirm = async () => {
    const row = deleteConfirmRun;
    if (!row) return;
    setDeleteConfirmRun(null);
    try {
      const res = await softDeleteRunMutation.mutateAsync(row.idLISRunId);
      const body = res?.data as RequestResponse<boolean> | undefined;
      const statusCode = Number(body?.statusCode);

      if (!statusCode || statusCode < 200 || statusCode >= 300) {
        toast.error(t(body?.message || "Unable to delete run"));
        return;
      }

      toast.success(t(body?.message || "Run deleted successfully."));
      setExpandedId(null);
      await loadData(curPage, pageSize, sort, searchRef.current);
    } catch (error: any) {
      toast.error(
        t(
          error?.response?.data?.message ||
          error?.message ||
          "Unable to delete run"
        )
      );
    }
  };

  const handlePrintPlate = (row: RunGridRow) => {
    const accessionSlots = runAccessionDrafts[row.id] ?? row.accessionSlots;
    printRunPlate({
      row,
      accessionSlots,
      t,
    });
  };

  const handleCreateRun = () => {
    const templateName = newTemplateName.trim();

    if (!templateName) {
      toast.error(t("Template Name is required."));
      return;
    }

    if (!newTemplateType?.value) {
      toast.error(t("Template Type is required."));
      return;
    }

    const plateSetupId = Number(newTemplateType.value);

    if (!Number.isFinite(plateSetupId) || plateSetupId <= 0) {
      toast.error(t("Invalid template type."));
      return;
    }

    createRunMutation.mutate({
      plateSetupId,
      templateName,
    });
  };

  const handleSearchRuns = () => {
    setCurPage(1);
    loadData(1, pageSize, sort, searchRef.current);
  };

  const handleTagRemoval = (clickedTag: string) => {
    const field = RUN_FILTER_FIELDS.find(
      (f) => t(RUN_FILTER_LABELS[f]) === clickedTag
    );
    if (!field) return;
    const next = { ...search, [field]: "" };
    setSearch(next);
    searchRef.current = next;
    setCurPage(1);
    loadData(1, pageSize, sort, next);
  };

  const handleSort = (column: string) => {
    setSort((prev) => {
      const nextOrder =
        prev.clickedIconData === column && prev.sortingOrder === "asc"
          ? "desc"
          : "asc";
      const next: SortingTypeI = {
        sortingOrder: nextOrder,
        clickedIconData: column,
      };
      loadData(1, pageSize, next, searchRef.current);
      setCurPage(1);
      return next;
    });
  };

  const handleReset = () => {
    const cleared = { ...initialSearch };
    setSearch(cleared);
    searchRef.current = cleared;
    setSort({ ...sortById });
    setCurPage(1);
    setPageSize(50);
    loadData(1, 50, { ...sortById }, cleared);
  };

  const handleTabsChange = (_event: React.SyntheticEvent, newValue: number) => {
    const cleared = { ...initialSearch };
    setTabIndex(newValue);
    setExpandedId(null);
    setShowAddPanel(false);
    setDeleteConfirmRun(null);
    setSearch(cleared);
    searchRef.current = cleared;
    setCurPage(1);
    loadData(1, pageSize, sort, cleared, newValue);
  };

  const renderSortHeader = (label: string, column: string) => (
    <div
      onClick={() => handleSort(column)}
      className="d-flex justify-content-between cursor-pointer"
      style={{ userSelect: "none" }}
    >
      <div style={{ width: "max-content" }}>{t(label)}</div>
      <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
        <ArrowUp
          CustomeClass={`${sort.sortingOrder === "desc" && sort.clickedIconData === column
              ? "text-success fs-7"
              : "text-gray-700 fs-7"
            } p-0 m-0`}
        />
        <ArrowDown
          CustomeClass={`${sort.sortingOrder === "asc" && sort.clickedIconData === column
              ? "text-success fs-7"
              : "text-gray-700 fs-7"
            } p-0 m-0`}
        />
      </div>
    </div>
  );

  const renderRunDetail = (row: RunGridRow) => {
    return (
      <RunDetailPanel
        row={row}
        tabIndex={tabIndex}
        t={t}
        runAccessionDrafts={runAccessionDrafts}
        runAccessionErrors={runAccessionErrors}
        setRunAccessionDrafts={setRunAccessionDrafts}
        setRunAccessionErrors={setRunAccessionErrors}
        onApplyAccessions={handleApplyAccessions}
        onPrintPlate={handlePrintPlate}
        onCompleteRun={handleCompleteRun}
        isApplyAccessionsLoading={applyAccessionsMutation.isLoading}
        isCompleteRunLoading={completeRunMutation.isLoading}
      />
    );
  };

  const tabBaseProps = {
    t,
    pageSize,
    setPageSize,
    setCurPage,
    showAddPanel,
    onToggleAddPanel: () => setShowAddPanel((prev) => !prev),
    onSearchRuns: handleSearchRuns,
    onReset: handleReset,
    isRunsLookupError,
    runsLookupError,
    runsListError,
    searchedTags,
    onTagRemoval: handleTagRemoval,
    search,
    setSearch,
    runTypeOptions,
    runTemplateOptions,
    renderSortHeader,
    isRunsLoading,
    runRows,
    expandedId,
    setExpandedId,
    onDeleteRun: handleDeleteRun,
    isDeletingRun: softDeleteRunMutation.isLoading,
    renderRunDetail,
    curPage,
    nextPage,
    pageNumbers,
    prevPage,
    showPage,
    total,
    totalPages,
  };

  return (
    <>
      <div className="app-toolbar py-2 py-lg-3">
        <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
          <BreadCrumbs />
        </div>
      </div>

      <div className="d-flex flex-column flex-column-fluid">
        <div className="app-content flex-column-fluid">
          <div className="app-container container-fluid">
            <CreateRunPanel
              t={t}
              show={showAddPanel}
              isRunTemplatesError={isRunTemplatesError}
              runTemplatesError={runTemplatesError}
              isRunTemplatesLoading={isRunTemplatesLoading}
              templateTypeOptions={templateTypeOptions}
              isCreateRunLoading={createRunMutation.isLoading}
              newTemplateName={newTemplateName}
              onTemplateNameChange={setNewTemplateName}
              newTemplateType={newTemplateType}
              onTemplateTypeChange={setNewTemplateType}
              onCancel={() => {
                setShowAddPanel(false);
                resetCreateRunForm();
              }}
              onCreateRun={handleCreateRun}
            />

            {/* ── Tabs (ManageSalesRep pattern: MUI Tabs + TabSelected) ───────── */}
            <Tabs
              value={tabIndex}
              onChange={handleTabsChange}
              TabIndicatorProps={{ style: { background: "transparent" } }}
              className="min-h-auto"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                "& .MuiTabs-scrollButtons": {
                  width: 0,
                  transition: "width 0.7s ease",
                  "&:not(.Mui-disabled)": {
                    width: "48px",
                  },
                },
              }}
            >
              {availableTabs.map((tab) => (
                <TabSelected
                  key={tab.value}
                  label={t(tab.label)}
                  {...a11yProps(tab.value)}
                  className="fw-bold text-capitalize"
                />
              ))}
            </Tabs>

            <div className="card">
              <div
                className="card-body py-2"
                id={`id-view-create-run-tabpanel-${tabIndex}`}
                role="tabpanel"
                aria-labelledby={
                  tabIndex === 0
                    ? "IDViewRunTabViewRuns"
                    : tabIndex === 1
                      ? "IDViewRunTabCompleteRuns"
                      : "IDViewRunTabDeletedRuns"
                }
              >
                {tabIndex === 0 ? (
                  <ViewRunsTab {...tabBaseProps} />
                ) : tabIndex === 1 ? (
                  <CompleteRunsTab {...tabBaseProps} />
                ) : (
                  <DeletedRunsTab {...tabBaseProps} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={deleteConfirmRun !== null}
        onHide={() => setDeleteConfirmRun(null)}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton className="bg-light-primary m-0 p-5">
          <h4>{t("Delete Record")}</h4>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to delete this record ?")}
        </Modal.Body>
        <Modal.Footer className="p-0">
          <button
            id="IDViewCreateRunDeleteModalCancel"
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteConfirmRun(null)}
          >
            {t("Cancel")}
          </button>
          <button
            id="IDViewCreateRunDeleteModalConfirm"
            type="button"
            className="btn btn-danger m-2"
            onClick={handleDeleteConfirm}
          >
            {t("Delete")}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default IDViewCreateRun;
